import { useState, useEffect, useRef } from 'react';
import { OKX, OYL, UNISAT, XVERSE } from '@omnisat/lasereyes-core';
import { useLaserEyes, WalletIcon } from '@omnisat/lasereyes-react';
import { useLang } from '../context/LanguageContext';
import { useToast } from './Toast';
import { classifyWalletError } from '../utils/walletError';
import { UnisatGlyph } from '../config/design';
import ReadMeModal from './ReadMeModal';

// Desktop offers OYL on top of Xverse/Unisat. The mobile sheet intentionally omits
// OYL - it's a browser/desktop extension with no mobile app deep-link, so there's
// nothing to "open in app" on a phone. Keep the lists divergent on purpose.
// Glyphs are the official wallet logos shipped by LaserEyes (WalletIcon), except
// Unisat which is inlined (UnisatGlyph) to avoid LaserEyes' shared-SVG-id collision.
const WALLETS = [
  { key: 'okx',    constant: OKX,    name: 'OKX',    glyph: <WalletIcon size={48} walletName={OKX} />,    installUrl: 'https://web3.okx.com/' },
  { key: 'oyl',    constant: OYL,    name: 'Oyl',    glyph: <WalletIcon size={48} walletName={OYL} />,    installUrl: 'https://oyl.io' },
  { key: 'xverse', constant: XVERSE, name: 'Xverse', glyph: <WalletIcon size={48} walletName={XVERSE} />, installUrl: 'https://www.xverse.app' },
  { key: 'unisat', constant: UNISAT, name: 'Unisat', glyph: <UnisatGlyph size={48} />,                    installUrl: 'https://unisat.io' },
];

/* ── single wallet row (handles its own hover) ─────────────── */
function WalletRow({ wallet, t, onPick, installed, connecting, busy }) {
  const [hover, setHover] = useState(false);
  const lit = installed && hover && !busy;
  const actionable = installed && !busy;
  const activate = () => { if (actionable) onPick(wallet); };

  return (
    <div
      data-wallet-row
      role="button"
      aria-label={wallet.name}
      aria-disabled={!actionable}
      tabIndex={actionable ? 0 : -1}
      onClick={activate}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); activate(); } }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onFocus={() => setHover(true)}
      onBlur={() => setHover(false)}
      style={{
        display: 'grid',
        gridTemplateColumns: '48px 1fr auto',
        gap: 16,
        alignItems: 'center',
        padding: '12px 14px',
        borderRadius: 16,
        background: 'var(--sunk)',
        boxShadow: lit ? 'inset 0 0 0 1.5px var(--ink)' : 'none',
        cursor: actionable ? 'pointer' : 'default',
        opacity: busy && !connecting ? 0.4 : 1,
        outline: 'none',
        transition: 'box-shadow .12s',
      }}
    >
      <div style={{
        width: 48, height: 48, borderRadius: 12, overflow: 'hidden',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'var(--card)',
      }}>
        {wallet.glyph}
      </div>

      <div style={{
        fontFamily: 'var(--f-body)',
        fontWeight: 700,
        fontSize: 17,
        lineHeight: 1.2,
        color: installed ? 'var(--ink)' : 'var(--dim)',
      }}>
        {wallet.name}
      </div>

      {connecting ? (
        <div style={{
          width: 20, height: 20, borderRadius: '50%',
          border: '2px solid var(--rule)', borderTopColor: 'var(--ink)',
          animation: 'mob-spin .8s linear infinite',
        }} />
      ) : installed ? (
        <span style={{
          fontFamily: 'var(--f-body)', fontSize: 11, fontWeight: 700, letterSpacing: 0.4,
          color: '#fff', background: 'var(--rind)', borderRadius: 999,
          padding: '3px 10px', whiteSpace: 'nowrap',
        }}>
          {t.modal_installed}
        </span>
      ) : (
        <a
          href={wallet.installUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          style={{
            fontFamily: 'var(--f-body)', fontWeight: 800, fontSize: 12, letterSpacing: 0.2,
            color: 'var(--ink)', textDecoration: 'underline', textUnderlineOffset: 2, whiteSpace: 'nowrap',
          }}
        >
          {t.modal_install}
        </a>
      )}
    </div>
  );
}

export default function ConnectWalletModal({ open, onClose, onConnect }) {
  const [readMeOpen, setReadMeOpen] = useState(false);
  const [connecting, setConnecting] = useState(null);   // wallet key currently connecting
  const dialogRef = useRef(null);
  const { t } = useLang();
  const { showToast } = useToast();
  const { hasOyl, hasXverse, hasUnisat, hasOkx } = useLaserEyes();

  // Reactive LaserEyes detection, with a direct window.* fallback in case a
  // provider injects before LaserEyes flags catch up.
  const detected = {
    oyl:    hasOyl    || (typeof window !== 'undefined' && !!window.oyl),
    xverse: hasXverse || (typeof window !== 'undefined' && !!window.XverseProviders),
    unisat: hasUnisat || (typeof window !== 'undefined' && !!window.unisat),
    okx:    hasOkx    || (typeof window !== 'undefined' && !!window.okxwallet),
  };

  // fresh state every open (modal stays mounted, just returns null when closed)
  useEffect(() => {
    if (open) { setReadMeOpen(false); setConnecting(null); }
  }, [open]);

  // ESC to close (blocked mid-connect, like the backdrop)
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape' && !connecting) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, connecting, onClose]);

  // lock background page scroll while open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // initial focus + focus-trap (paused while ReadMe is layered on top)
  useEffect(() => {
    if (!open || readMeOpen) return;
    const node = dialogRef.current;
    if (!node) return;
    const focusables = () => Array.from(
      node.querySelectorAll('[role="button"], button, a[href], [tabindex]')
    ).filter(el => el.tabIndex !== -1 && !el.disabled && el.offsetParent !== null);
    // focus the first installed wallet if any, else the first focusable control
    const firstWallet = node.querySelector('[data-wallet-row][tabindex="0"]');
    (firstWallet || focusables()[0] || node).focus();
    const onKey = (e) => {
      if (e.key !== 'Tab') return;
      const f = focusables();
      if (!f.length) return;
      const first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    node.addEventListener('keydown', onKey);
    return () => node.removeEventListener('keydown', onKey);
  }, [open, readMeOpen]);

  if (!open) return null;

  const pick = async (wallet) => {
    if (connecting) return;
    setConnecting(wallet.key);
    try {
      await onConnect(wallet.constant);   // App closes the modal once `connected` flips
    } catch (e) {
      // wallet errors are arbitrary external strings → localize the common cases
      const msg = classifyWalletError(e) === 'rejected' ? t.modal_errRejected : t.modal_connectFailed;
      showToast(msg, { type: 'error' });
      setConnecting(null);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(52,52,51,.28)',
      animation: 'modalScrimIn .2s ease',
    }}>
      {/* click-away backdrop (disabled mid-connect) */}
      <div onClick={connecting ? undefined : onClose} style={{ position: 'absolute', inset: 0 }} />

      {/* white dialog */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Connect wallet"
        tabIndex={-1}
        className="cw-dialog"
        style={{
          position: 'relative', width: 460, maxWidth: '94vw', borderRadius: 24, overflow: 'hidden',
          background: 'var(--card)', boxShadow: '0 1px 2px rgba(38,32,26,.04), inset 0 0 0 1px var(--rule)',
          animation: 'modalPopIn .26s cubic-bezier(.22,1,.36,1)',
        }}
      >
        <div style={{ position: 'relative' }}>
          {/* header */}
          <div style={{ position: 'relative', padding: '24px 24px 6px' }}>
            <div style={{ fontFamily: 'var(--f-body)', fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: 'var(--faint)' }}>
              {t.modal_eyebrow}
            </div>
            <div style={{ fontFamily: 'var(--f-body)', fontWeight: 800, fontSize: 26, letterSpacing: -0.6, lineHeight: 1.1, marginTop: 6, color: 'var(--ink)' }}>
              {t.modal_title1} {t.modal_title2}
            </div>
            <div style={{ marginTop: 6, fontFamily: 'var(--f-body)', fontSize: 13, color: 'var(--dim)' }}>
              {t.modal_subtitle}
            </div>

            <button
              onClick={connecting ? undefined : onClose}
              aria-label="Close"
              disabled={!!connecting}
              className="cw-x"
              style={{
                position: 'absolute', top: 20, right: 20, width: 36, height: 36, borderRadius: 999,
                border: 'none', background: 'var(--card)', color: 'var(--ink)',
                fontSize: 16, lineHeight: 1, cursor: connecting ? 'default' : 'pointer',
                opacity: connecting ? 0.4 : 1,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              ✕
            </button>
          </div>

          {/* wallet list */}
          <div style={{ padding: '14px 14px 4px', display: 'flex', flexDirection: 'column', gap: 6 }}>
            {WALLETS.map((w) => (
              <WalletRow
                key={w.key}
                wallet={w}
                t={t}
                onPick={pick}
                installed={!!detected[w.key]}
                connecting={connecting === w.key}
                busy={!!connecting}
              />
            ))}
          </div>

          {/* footer */}
          <div style={{
            marginTop: 8, padding: '14px 24px 18px',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 14,
          }}>
            <div style={{ fontFamily: 'var(--f-body)', fontSize: 12, color: 'var(--dim)' }}>
              {t.modal_termsPre}{' '}
              <button
                onClick={() => setReadMeOpen(true)}
                style={{
                  background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                  fontFamily: 'var(--f-body)', fontWeight: 800, fontSize: 12, color: 'var(--ink)',
                  textDecoration: 'underline', textUnderlineOffset: 2,
                }}
              >
                {t.modal_termsLink}
              </button>.
            </div>
            <div style={{ fontFamily: 'var(--f-mono)', fontSize: 11, color: 'var(--faint)' }}>v0.3</div>
          </div>
        </div>
      </div>

      <ReadMeModal open={readMeOpen} onClose={() => setReadMeOpen(false)} />
    </div>
  );
}
